// First-login welcome ("Welcome to scoutm8, <name>"), then a guided first search; shown once per account.
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";
import { FiArrowRight } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import { loadGetStarted, markGetStarted } from "../lib/getStartedService";
import { getUserSettings } from "../lib/userSettingsService";
import { usePlan } from "../utils/usePlan";
import { startTour } from "./GuidedTour";
import { releaseBootSplash } from "../utils/bootSplash";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: none; }
`;
const letterIn = keyframes`
  from { opacity: 0; transform: translateY(0.35em) rotate(4deg); filter: blur(8px); }
  to { opacity: 1; transform: none; filter: blur(0); }
`;
const draw = keyframes`
  from { stroke-dashoffset: 1; }
  to { stroke-dashoffset: 0; }
`;
const drift = keyframes`
  0% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(4%, -3%) scale(1.08); }
  100% { transform: translate(0, 0) scale(1); }
`;
const fadeOut = keyframes`
  to { opacity: 0; transform: scale(1.015); }
`;

const Screen = styled.div`
  position: fixed;
  inset: 0;
  z-index: 19000;
  display: grid;
  place-items: center;
  padding: 24px 16px;
  overflow: hidden;
  background: ${({ theme }) => theme.bg};
  ${({ $leaving }) =>
    $leaving &&
    css`
      animation: ${fadeOut} 0.45s ease-in forwards;
      pointer-events: none;
    `}
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-delay: 0ms !important;
    }
  }
`;

// Two slow, soft brand glows behind the words.
const Glow = styled.div`
  position: absolute;
  width: 70vmax;
  height: 70vmax;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.55;
  pointer-events: none;
  animation: ${drift} 18s ease-in-out infinite;
  ${({ $tone, theme }) =>
    $tone === "orange"
      ? css`
          right: -28vmax;
          bottom: -34vmax;
          background: ${theme.secondarySoft};
          animation-delay: -6s;
        `
      : css`
          left: -26vmax;
          top: -32vmax;
          background: ${theme.accentSoft};
        `}
`;

const Inner = styled.div`
  position: relative;
  max-width: 880px;
  text-align: center;
`;

const Hello = styled.h1`
  margin: 0;
  font-family: "Instrument Serif", Georgia, serif;
  font-weight: 400;
  font-size: clamp(44px, 8vw, 104px);
  line-height: 1.02;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.ink};
  .lead {
    display: block;
    opacity: 0;
    animation: ${fadeUp} 0.9s cubic-bezier(0.2, 0.7, 0.2, 1) 0.2s forwards;
  }
  .name {
    position: relative;
    display: inline-block;
    font-style: italic;
    color: ${({ theme }) => theme.accent};
    white-space: nowrap;
  }
  .ch {
    display: inline-block;
    opacity: 0;
    animation: ${letterIn} 0.7s cubic-bezier(0.2, 0.7, 0.2, 1) forwards;
  }
  svg {
    position: absolute;
    left: -2%;
    bottom: -0.12em;
    width: 104%;
    height: 0.28em;
    overflow: visible;
    path {
      fill: none;
      stroke: ${({ theme }) => theme.secondary};
      stroke-width: 3;
      stroke-linecap: round;
      stroke-dasharray: 1;
      stroke-dashoffset: 1;
      animation: ${draw} 0.8s ease-out forwards;
    }
  }
`;

const Sub = styled.p`
  margin: 36px auto 0;
  max-width: 520px;
  font-size: clamp(16px, 1.6vw, 19px);
  line-height: 1.55;
  color: ${({ theme }) => theme.inkSoft || theme.inkMuted};
  opacity: 0;
  animation: ${fadeUp} 0.8s ease-out forwards;
`;

const Actions = styled.div`
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  opacity: 0;
  animation: ${fadeUp} 0.8s ease-out forwards;
  .go {
    appearance: none;
    height: 52px;
    padding: 0 28px;
    border-radius: 999px;
    border: 1px solid ${({ theme }) => theme.accent};
    background: ${({ theme }) => theme.accent};
    color: ${({ theme }) => theme.onFill || "#fff"};
    font: inherit;
    font-size: 16px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    box-shadow: ${({ theme }) => theme.shadowMd};
    transition: transform 0.15s, background 0.15s;
    svg { transition: transform 0.15s; }
    &:hover {
      background: ${({ theme }) => theme.accentHover || theme.accent};
      svg { transform: translateX(3px); }
    }
    &:active { transform: translateY(1px); }
  }
  .skip {
    appearance: none;
    border: 0;
    background: none;
    padding: 4px 8px;
    font: inherit;
    font-size: 13.5px;
    color: ${({ theme }) => theme.inkMuted};
    cursor: pointer;
    &:hover { color: ${({ theme }) => theme.ink}; }
  }
`;

// Timings (s): the lead line, then the name letter by letter, then the rest.
const NAME_START = 0.85;
const LETTER_STEP = 0.07;

export default function WelcomeScreen() {
  const { user } = useAuth();
  const sub = user?.sub;
  const navigate = useNavigate();
  const { status, features } = usePlan();
  // "pending" draws nothing: the boot splash covers the app until we know whether this login gets the welcome.
  const [phase, setPhase] = useState("pending");
  const [name, setName] = useState("");

  useEffect(() => {
    if (!sub) return undefined;
    let live = true;
    Promise.all([loadGetStarted(), getUserSettings(sub).catch(() => null)]).then(([gs, settings]) => {
      if (!live) return;
      // Decided either way: the boot splash hands off to the welcome or straight to the page.
      releaseBootSplash();
      if (!gs?.showOnLogin) return setPhase("off");
      setName(String(settings?.firstName || "").trim());
      setPhase("show");
      markGetStarted({ seen: true });
    });
    return () => {
      live = false;
    };
  }, [sub]);

  if (phase === "pending" || phase === "off") return null;

  const leave = (then) => {
    setPhase("leaving");
    setTimeout(() => {
      setPhase("off");
      then?.();
    }, 450);
  };
  const findCandidate = () => {
    navigate("/find-candidates");
    leave(() => startTour("people"));
  };

  const onTrial = features.plan === "trial" || status?.status === "TRIAL";
  const letters = Array.from(name);
  const restAt = NAME_START + letters.length * LETTER_STEP + 0.9;

  return createPortal(
    <Screen $leaving={phase === "leaving"} role="dialog" aria-modal="true" aria-label="Welcome to scoutm8">
      <Glow />
      <Glow $tone="orange" />
      <Inner>
        <Hello>
          <span className="lead">Welcome to scoutm8{letters.length ? "," : "."}</span>
          {letters.length > 0 && (
            <span className="name" aria-label={name}>
              {letters.map((c, i) => (
                <span
                  key={i}
                  className="ch"
                  aria-hidden="true"
                  style={{ animationDelay: `${NAME_START + i * LETTER_STEP}s` }}
                >
                  {c}
                </span>
              ))}
              <svg viewBox="0 0 100 10" preserveAspectRatio="none" aria-hidden="true">
                <path
                  pathLength="1"
                  d="M2 7 C 25 2, 55 2, 98 5"
                  style={{ animationDelay: `${NAME_START + letters.length * LETTER_STEP + 0.1}s` }}
                />
              </svg>
            </span>
          )}
        </Hello>
        <Sub style={{ animationDelay: `${restAt}s` }}>
          {onTrial
            ? "Your free trial is ready. Let's start where it counts: finding your first candidate."
            : "Let's start where it counts: finding your first candidate."}
        </Sub>
        <Actions style={{ animationDelay: `${restAt + 0.35}s` }}>
          <button type="button" className="go" onClick={findCandidate} autoFocus>
            Find a candidate <FiArrowRight size={18} aria-hidden="true" />
          </button>
          <button type="button" className="skip" onClick={() => leave()}>
            Skip to dashboard
          </button>
        </Actions>
      </Inner>
    </Screen>,
    document.body,
  );
}
